import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Permanently-seeded item fixtures for the dl-63583 epic — "D2 Playwright automation: dl-58415
 * items-meta endpoint for custom fields" (`e2e/src/pages/items/items-meta/*.spec.ts`).
 *
 * These are REAL entries in `itemsData` (spliced in from `items.ts`), not `apiTransform`
 * payloads, for two reasons:
 *
 * 1. **The `apiTransform` fixture holds exactly one override at a time** (a single
 *    `localStorage['api-transform']` key — see `e2e/src/fixtures/index.ts` and
 *    `frontend/.claude/rules/playwright-mocks.md`: "safe to use one override per test; it does
 *    not stack"). A test that needs to shape BOTH the meta response (`/api/meta/items/customfields`)
 *    AND a specific item's values cannot do both via two `apiTransform` calls — the second call
 *    silently replaces the first. Seeding the item permanently frees `apiTransform` for the one
 *    thing that actually needs per-test shaping: the meta response.
 * 2. **Save-flow specs need a real, findable row.** `PUT /api/items/:id` and the bulk
 *    `PUT /api/items` look the item up by id in the live `itemsData` array (see
 *    `auditLogFixtureItem`'s header comment for the identical reasoning) — a purely
 *    `apiTransform`-fabricated item 404s on save.
 *
 * Isolable via `?search=ItemsMeta` (unique substring), with a sub-word per scenario
 * (`?search=ItemsMeta Probe`, `?search=ItemsMeta Mixed`, …) for narrowing to one test's own rows.
 *
 * `customFields[]` entries here carry the SAME (heavier, metadata-embedding) shape every other
 * fixture in this mock uses (`itemsClearFieldFixtures.ts`'s `ItemCustomFieldEntry`) purely so
 * TypeScript's inferred `itemsData` element type accepts them when spliced in — the Items feature
 * never reads these extra fields at runtime (`apiDefinitions` is always explicitly passed to
 * `useCustomFieldColumns`, so Items never falls back to extracting metadata off a row's own
 * `customFields`; see `itemsMetaFrontendIntegrity.spec.ts` #621817/#621822/#621832). Specs that
 * pin the real, POST-refactor "value-only, no metadata" wire contract (#621804) build their OWN
 * literal, minimal-shape payload via a single `apiTransform` override instead of reading these
 * fixtures — see that spec for why.
 */

type CfEntry = {
  id: number;
  customFieldId: number;
  name: string;
  userDefinedId: string | null;
  type: number;
  value: string | null;
  itemCustomFieldAllowedValueId: number | null;
  customFieldAllowedValueUserDefinedID: string | null;
  isMandatory: boolean;
  isSupplierEditable: boolean;
  internalOnly: boolean;
  maxChar: number | null;
  isMultiLine: boolean;
  allowedDecimals: number;
  isMultipleAllowedValue: boolean;
  parentCustomFieldId: number | null;
  parentCustomFieldName: string | null;
};

/** Builds one `customFields[]` entry. `name`/`type`/etc. mirror the matching definition in the
 *  default `itemCustomFieldDefinitions` (`itemsFixtureData.ts`) purely for internal consistency —
 *  Items never reads them off the row (see file header). */
const cf = (
  id: number,
  customFieldId: number,
  name: string,
  type: number,
  value: string | null,
  itemCustomFieldAllowedValueId: number | null = null
): CfEntry => ({
  id,
  customFieldId,
  name,
  userDefinedId: null,
  type,
  value,
  itemCustomFieldAllowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
});

const base = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-02-01T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
};

/**
 * The general-purpose "kitchen sink" probe — carries a value for every displayable CF type plus
 * both misalignment cases (id 40 / id 999 orphan values, no definition anywhere). Used by every
 * items-meta test that only needs to vary the META response and read this one item's values.
 */
export const itemsMetaProbe = {
  ...base,
  id: 880010,
  name: 'ItemsMeta Probe Item',
  userDefinedId: 'IT-880010',
  description: null,
  customFields: [
    cf(8800101, 8, 'Season used', 0, 'AW25', 26),
    cf(8800102, 9, 'Internal notes', 2, 'Probe note'),
    cf(8800103, 10, 'Care instruction', 0, 'Machine wash', 101),
    cf(8800104, 11, 'Launch date', 1, '2026-03-15'),
    cf(8800105, 13, 'Target weight', 4, '42.50'),
    cf(8800106, 14, 'Color family', 3, 'Warm', 501),
    cf(8800107, 15, 'Color shade', 3, 'Red', 601),
    cf(8800108, 40, 'Retired field (no definition)', 2, 'Legacy value from a deactivated field'),
    cf(8800109, 999, 'Unconfigured field (no definition)', 2, 'Unconfigured value'),
  ],
};

/** `PUT`-flow probe — starts with one value-only CF entry; specs mutate it via a real save. */
export const itemsMetaWriteProbe = {
  ...base,
  id: 880020,
  name: 'ItemsMeta Write Probe',
  userDefinedId: 'IT-880020',
  description: null,
  customFields: [cf(8800201, 9, 'Internal notes', 2, 'Before edit')],
};

/** Inline-edit persistence probe (distinct from the write-round-trip probe above so the two
 *  specs' saves can never collide on the same row). */
export const itemsMetaInlineEditProbe = {
  ...base,
  id: 880030,
  name: 'ItemsMeta InlineEdit Probe',
  userDefinedId: 'IT-880030',
  description: null,
  customFields: [cf(8800301, 9, 'Internal notes', 2, 'Initial value')],
};

/** Ready-for-export + mandatory-field probe (Testmo #621819/#621820). */
export const itemsMetaMandatoryExportProbe = {
  ...base,
  id: 880040,
  name: 'ItemsMeta MandatoryExport Probe',
  userDefinedId: 'IT-880040',
  description: null,
  readyForExport: true,
  customFields: [cf(8800401, 9, 'Internal notes', 2, 'Existing mandatory value')],
};

/** Bulk-edit Mixed-values probe pair (Testmo #621808/#621823) — differing "Internal notes". */
export const itemsMetaMixedProbeA = {
  ...base,
  id: 880050,
  name: 'ItemsMeta Mixed Probe A',
  userDefinedId: 'IT-880050',
  description: null,
  customFields: [cf(8800501, 9, 'Internal notes', 2, 'Note A')],
};
export const itemsMetaMixedProbeB = {
  ...itemsMetaMixedProbeA,
  id: 880051,
  name: 'ItemsMeta Mixed Probe B',
  userDefinedId: 'IT-880051',
  customFields: [cf(8800511, 9, 'Internal notes', 2, 'Note B')],
};

/** Confirm-changes probe (Testmo #621824). */
export const itemsMetaConfirmChangesProbe = {
  ...base,
  id: 880060,
  name: 'ItemsMeta ConfirmChanges Probe',
  userDefinedId: 'IT-880060',
  description: null,
  customFields: [cf(8800601, 9, 'Internal notes', 2, 'Before confirm')],
};

/** Bulk-edit activity-log probe pair (Testmo #621825) — a real PUT round trip per item. */
export const itemsMetaAuditProbeA = {
  ...base,
  id: 880070,
  name: 'ItemsMeta Audit Probe A',
  userDefinedId: 'IT-880070',
  description: null,
  customFields: [cf(8800701, 9, 'Internal notes', 2, 'Audit before A')],
};
export const itemsMetaAuditProbeB = {
  ...itemsMetaAuditProbeA,
  id: 880071,
  name: 'ItemsMeta Audit Probe B',
  userDefinedId: 'IT-880071',
  customFields: [cf(8800711, 9, 'Internal notes', 2, 'Audit before B')],
};

/** Bulk-edit search-bar + multi-select-replace probe pair (Testmo #621826/#621829). */
export const itemsMetaSearchProbeA = {
  ...base,
  id: 880080,
  name: 'ItemsMeta Search Probe A',
  userDefinedId: 'IT-880080',
  description: null,
  customFields: [cf(8800801, 8, 'Season used', 0, 'AW25', 26)],
};
export const itemsMetaSearchProbeB = {
  ...itemsMetaSearchProbeA,
  id: 880081,
  name: 'ItemsMeta Search Probe B',
  userDefinedId: 'IT-880081',
  customFields: [cf(8800811, 8, 'Season used', 0, 'SS25', 27)],
};

/** Every items-meta epic probe item, in seed order — spread into `items.ts`'s `itemsData`. */
export const itemsMetaFixtureItems = [
  itemsMetaProbe,
  itemsMetaWriteProbe,
  itemsMetaInlineEditProbe,
  itemsMetaMandatoryExportProbe,
  itemsMetaMixedProbeA,
  itemsMetaMixedProbeB,
  itemsMetaConfirmChangesProbe,
  itemsMetaAuditProbeA,
  itemsMetaAuditProbeB,
  itemsMetaSearchProbeA,
  itemsMetaSearchProbeB,
];

/**
 * Ids of this epic's own rows whose bulk-edit custom-field changes record an audit entry
 * (dl-63583, Testmo #621825). The bulk `PUT /api/items` audit path is scoped to exactly these
 * rows on purpose: the merged dl-62706 "Bulk edit – Clear fields" suite
 * (`items/bulk-edit/clear-fields/dependency-and-apply-audit.spec.ts`, Testmo #2339512) asserts
 * the Items bulk PUT records **no** audit entries for its own rows, so auditing every row would
 * break that already-shipped spec. Keeping the audit opt-in to items-meta rows lets both
 * contracts hold on the one shared handler.
 */
export const itemsMetaBulkAuditItemIds = new Set(itemsMetaFixtureItems.map(item => item.id));
